import { SendSmsLenkeRequest, SmsKanal, SmsLenketype } from "codegen";
import { useMemo, useState } from "react";
import { z } from "zod";

import { Direction, Flex } from "@ntemarked/mittne-components/dist/Flex/Flex";

import { MainLayoutPageHeader } from "components/organisms/MainLayout";
import Form from "components/organisms/form/Form";
import FormButton from "components/organisms/form/FormButton";
import SelectField from "components/organisms/form/SelectField";
import TextField from "components/organisms/form/TextField";
import { ToggleFieldClean } from "components/organisms/form/ToggleField";
import useProducts from "hooks/useProducts";
import { useStrombestillingApiMutation } from "hooks/useStrombestillingApi";
import { capitalizeFirstLetter } from "utils/Utils";

const sellableProductTariffIds = new Set([
	"standard-spotpris-privat",
	"kundekontrakt-webspot-6mnd",
	"standard-spotpris-bedrift",
	"agrol-inn",
	"agrol-spotpris-bedrift",
	"bli-spot",
]);

const ProductLinkSchema = z.object({
	phone: z.string().min(1),
	product: z.string().min(1),
	channel: z.nativeEnum(SmsKanal),
});

const NorgesprisSchema = z.object({
	phone: z.string().min(1),
});

// Samme lenke som backend sender (norgespris.bestilling.url i strombestilling-api)
const norgesprisUrl = "https://minside.elhub.no/norgespris";

export function SendOrderLinkPage() {
	const [norgespris, setNorgespris] = useState(false);

	return (
		<Flex style={{ maxWidth: "800px" }} gap="1rem" direction={Direction.column}>
			<MainLayoutPageHeader title="Send bestillingslenke" />
			<ToggleFieldClean name="norgespris" label="Norgespris" checked={norgespris} setValue={setNorgespris} />
			{norgespris ? <NorgesprisLinkForm /> : <ProductLinkForm />}
		</Flex>
	);
}

function useSendSmsLink() {
	const mutation = useStrombestillingApiMutation("/sms/lenke", [], "POST");
	const [okResult, setOkResult] = useState(false);
	const [error, setError] = useState(false);

	async function send(body: SendSmsLenkeRequest) {
		setOkResult(false);
		setError(false);
		try {
			const response = await mutation.mutateAsync({ ...body, mobil: body.mobil.replace(/\s/g, "") });
			if (!response.ok) {
				setError(true);
				return;
			}
			setOkResult(true);
		} catch {
			setError(true);
		}
	}

	return { send, okResult, error, loading: mutation.isPending };
}

function SendStatus({ okResult, error, loading }: { readonly okResult: boolean; readonly error: boolean; readonly loading: boolean }) {
	return (
		<>
			{error && <p>Noe gikk galt</p>}
			{okResult && !error && <p>Melding sendt!</p>}
			{loading && <p>Sender melding...</p>}
		</>
	);
}

function ProductLinkForm() {
	const { send, okResult, error, loading } = useSendSmsLink();
	const [selectedProduct, setSelectedProduct] = useState("");

	const currentURL = useMemo(() => {
		if (!selectedProduct) return "Velg produkt for å generere lenke";
		return "https://strombestilling.nte.no/?produkt=" + selectedProduct + "&utm_source=" + SmsKanal.Epost;
	}, [selectedProduct]);

	const {
		productSelectOptions: productOptions,
		isLoadingProducts,
		isRefetchingProducts,
	} = useProducts({ productOptionsTariffIds: sellableProductTariffIds });

	if (isLoadingProducts || isRefetchingProducts) return;
	return (
		<Form
			onSubmit={(data) => send({ mobil: data.phone, lenketype: SmsLenketype.Bestilling, produktId: data.product, kanal: data.channel })}
			schema={ProductLinkSchema}
			loading={isLoadingProducts}
		>
			<Flex direction={Direction.column} gap="14px">
				<TextField name="phone" label="Telefonnummer" type="tel" autoComplete="off" />
				<SelectField
					label="Produkt"
					name="product"
					options={[{ label: "Velg produkt", value: "" }, ...productOptions]}
					onChange={(v) => setSelectedProduct(v)}
				/>
				<SelectField
					label="Salgskanal"
					name="channel"
					defaultValue={SmsKanal.Telefon}
					options={Object.values(SmsKanal).map((p) => ({
						value: p,
						label: capitalizeFirstLetter(p.substring(3).toLocaleLowerCase()),
					}))}
				/>
				<TextField label="Link for epost" name="emailUrl" value={currentURL} disabled={true} />
				<FormButton loading={loading} label="Send sms" />
				<SendStatus okResult={okResult} error={error} loading={loading} />
			</Flex>
		</Form>
	);
}

function NorgesprisLinkForm() {
	const { send, okResult, error, loading } = useSendSmsLink();

	return (
		<Form onSubmit={(data) => send({ mobil: data.phone, lenketype: SmsLenketype.Norgespris })} schema={NorgesprisSchema}>
			<Flex direction={Direction.column} gap="14px">
				<TextField name="phone" label="Telefonnummer" type="tel" autoComplete="off" />
				<TextField label="Link for epost" name="emailUrl" value={norgesprisUrl} disabled={true} />
				<FormButton loading={loading} label="Send sms" />
				<SendStatus okResult={okResult} error={error} loading={loading} />
			</Flex>
		</Form>
	);
}
